// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show, createEffect, createMemo, createSignal } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { useLocation } from "@tanstack/solid-router";
import ChevronDown from "lucide-solid/icons/chevron-down";
import Cloud from "lucide-solid/icons/cloud";
import Server from "lucide-solid/icons/server";
import type {
  ClusterBody,
  ClusterCreateInputBody,
  MemberBody,
  OperationLogBody,
  SessionBody,
} from "./api/generated";
import {
  approveOperation,
  billingPortal,
  billingStatus,
  createCluster,
  createProvider,
  deleteProvider,
  destroyCluster,
  getOperation,
  getOperationLogs,
  listClusterOperations,
  listClusterResources,
  listClusters,
  listMembers,
  listNotifications,
  listProjects,
  listProviderRegions,
  listProviders,
  listSessions,
  updateAccount,
  updateCluster,
  updateClusterProject,
} from "./api/generated";
import { authSlotHeaders } from "./api/runtime";
import { MembersPage } from "./accounts";
import { ClusterConnect } from "./cluster-connect";
import { Button, Field, Form, ValidationMessage } from "./components/forms";
import { Heading } from "./components/layout";
import { navigateExternal } from "./external-navigation";
import { AccessNotPermitted, useEntitlement } from "./entitlements";
import {
  accountSettingsFormSchema,
  accountSettingsFormValue,
  clusterFormSchema,
  clusterFormValue,
  clusterProjectFormSchema,
  providerFormSchema,
  providerFormValue,
} from "./form-validation";
import { AppLink, useGo } from "./navigation";
import { NotFound } from "./public-pages";
import { SessionsPage } from "./sessions";
import { Rows } from "./tables";

const clusterNetworks = Array.from(
  { length: 13 },
  (_, index) => `172.${18 + index}.0.0/16`,
);

const awsCloudFormationTemplateURL = import.meta.env.PROD
  ? "https://nadrama.s3.amazonaws.com/stack.json"
  : "https://nadrama-connect-development.s3.amazonaws.com/stack-dev.json";
const awsNadramaPrincipalArn = import.meta.env.PROD
  ? "arn:aws:iam::970547380382:root"
  : "arn:aws:iam::339713161177:root";
const awsCloudFormationURL =
  "https://console.aws.amazon.com/cloudformation/home?#/stacks/create/review" +
  `?templateURL=${encodeURIComponent(awsCloudFormationTemplateURL)}` +
  "&stackName=NadramaRole" +
  `&param_NadramaPrincipalArn=${encodeURIComponent(awsNadramaPrincipalArn)}`;

const resourceLabels: Record<string, string> = {
  aws_autoscaling_group: "Auto Scaling group",
  aws_launch_template: "launch template",
  aws_nat_gateway: "NAT gateway",
  aws_security_group: "security group",
  aws_subnet: "subnet",
  aws_vpc: "network",
};

function resourceLabel(event: OperationLogBody) {
  if (event.resourceType && resourceLabels[event.resourceType])
    return resourceLabels[event.resourceType];
  const value = event.resourceType ?? event.resourceAddress ?? "infrastructure";
  return value
    .replace(/^(aws|google|kubernetes|local|null|random)_/, "")
    .replaceAll("_", " ");
}

function resourceActivity(event: OperationLogBody) {
  const active: Record<string, string> = {
      create: "Creating",
      delete: "Removing",
      read: "Checking",
      replace: "Replacing",
      update: "Updating",
    },
    complete: Record<string, string> = {
      create: "Created",
      delete: "Removed",
      read: "Checked",
      replace: "Replaced",
      update: "Updated",
    },
    planned: Record<string, string> = {
      create: "Will create",
      delete: "Will remove",
      read: "Will check",
      replace: "Will replace",
      update: "Will update",
    },
    failed: Record<string, string> = {
      create: "Could not create",
      delete: "Could not remove",
      read: "Could not check",
      replace: "Could not replace",
      update: "Could not update",
    };
  const verbs =
      event.type === "planned_change"
        ? planned
        : event.type === "apply_complete"
          ? complete
          : event.type === "apply_errored"
            ? failed
            : active,
    suffix = ["planned_change", "apply_complete", "apply_errored"].includes(
      event.type,
    )
      ? ""
      : "…";
  return `${verbs[event.action ?? ""] ?? "Processing"} ${resourceLabel(event)}${suffix}`;
}

const activeOperationStates = [
  "enqueue_failed",
  "enqueued",
  "running",
  "waiting_approval",
];

function operationStateLabel(state: string) {
  const labels: Record<string, string> = {
    development_timeout: "Stopped",
    enqueue_failed: "Could not queue",
    enqueued: "Queued",
    failed: "Failed",
    running: "In progress",
    succeeded: "Succeeded",
    waiting_approval: "Waiting for approval",
  };
  return labels[state] ?? state.replaceAll("_", " ");
}

function providerLabel(provider: string) {
  const name = provider.match(/\/([^/"\]]+)"?\]?$/)?.[1] ?? provider;
  return name === "aws" ? "AWS" : name.replaceAll("_", " ");
}

function operationProgress(events: OperationLogBody[]) {
  const summary = events.findLast(
      (event) => event.type === "change_summary" && event.operation === "plan",
    ),
    total = summary
      ? (summary.addCount ?? 0) +
        (summary.changeCount ?? 0) +
        (summary.removeCount ?? 0)
      : 0,
    complete = events.filter((event) => event.type === "apply_complete").length,
    current = events.findLast((event) =>
      [
        "planned_change",
        "apply_start",
        "apply_progress",
        "apply_complete",
        "apply_errored",
      ].includes(event.type),
    );
  return { total, complete: Math.min(complete, total), current };
}

function ClusterWaitlistMessage() {
  return (
    <div role="note" class="panel max-w-3xl p-6 text-slate-600">
      <p>
        Your account is currently waitlisted for access. Please{" "}
        <a
          data-document-navigation
          class="link"
          href="https://nadrama.com/contact"
        >
          reach out
        </a>{" "}
        if you have a specific use case you’d like to test and provide feedback
        on.
      </p>
    </div>
  );
}

export function ProviderNew(p: {
  slot: number;
  account: string;
  kind?: string;
}) {
  const go = useGo();
  const base = `/u${p.slot}/${p.account}`;
  const queryClient = useQueryClient();
  const canManage = useEntitlement("account.settings.manage");
  if (!canManage())
    return (
      <>
        <Heading title="Connect a cloud provider" />
        <AccessNotPermitted>
          You don’t have permission to connect cloud providers. Ask an account
          administrator if you need access.
        </AccessNotPermitted>
      </>
    );
  const providerRegions = createQuery(() => ({
    queryKey: [p.slot, "provider-regions", p.kind],
    enabled: p.kind === "aws" || p.kind === "google",
    queryFn: async () =>
      (
        await listProviderRegions({
          throwOnError: true,
          responseStyle: "fields",
          path: { kind: p.kind as "aws" | "google" },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  if (!p.kind)
    return (
      <>
        <Heading
          title="Connect a cloud provider"
          copy="Choose the cloud account you want Nadrama to manage."
        />
        <div class="grid gap-4 md:grid-cols-2">
          <button
            class="panel p-6 text-left"
            onClick={() => go(`${base}/providers/new/aws`)}
          >
            <h2 class="text-xl font-bold">Amazon Web Services</h2>
            <p class="mt-2">Connect an AWS account using an IAM role.</p>
          </button>
          <button
            class="panel p-6 text-left"
            onClick={() => go(`${base}/providers/new/google`)}
          >
            <h2 class="text-xl font-bold">Google Cloud</h2>
            <p class="mt-2">Connect a Google Cloud project using a service account.</p>
          </button>
        </div>
      </>
    );
  if (!["aws", "google"].includes(p.kind)) return <NotFound />;
  const aws = p.kind === "aws";
  return (
    <>
      <Heading
        title={`Connect ${aws ? "Amazon Web Services" : "Google Cloud"}`}
        copy={
          aws
            ? "Once connected to your Amazon Web Services (AWS) account, Nadrama will run EC2 instances inside your Region/AZ and VPC/Subnet of choice. The main cost to you is typically the EC2s, NLB, and NAT, however additional AWS charges (such as for storage and bandwidth usage) will apply. You are responsible for your AWS costs."
            : "Once connected to your Google Cloud project, Nadrama can run Compute Engine VMs inside your Region, Zone, VPC, and Subnet of choice. The main cost to you is based on the VM instances, however additional Google Cloud charges (such as for storage and bandwidth usage) will apply. You are responsible for your Google Cloud costs."
        }
      />
      <section class="panel grid gap-5 p-6">
        <h2 class="text-xl font-bold">
          Step 1 of 2:{" "}
          {aws
            ? "Create IAM Role via Cloudformation in your AWS account"
            : "Create Service Account with Permissions in your Google Cloud project"}
        </h2>
        {aws ? (
          <>
            <a
              data-document-navigation
              class="w-fit"
              href={awsCloudFormationURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/images/aws/cloudformation-launch-stack-button.png"
                alt="Launch Stack"
              />
            </a>
            <p>
              Note: you must be logged-in to the AWS console for the above
              button to work. Once you click Launch Stack, click Next 3 times,
              check the ‘I acknowledge’ box and click ‘Submit’. Copy your
              Account ID, close the AWS Console tab/window and return here to
              Step 2.
            </p>
          </>
        ) : (
          <>
            <p>
              You’ll use the <em>gcloud</em> CLI tool to create a Service
              Account and role bindings for Nadrama to connect to your Google Cloud
              project.
            </p>
            <a
              data-document-navigation
              class="btn btn-secondary w-fit"
              target="_blank"
              rel="noopener noreferrer"
              href="https://shell.cloud.google.com/cloudshell/editor?cloudshell_git_repo=https%3A%2F%2Fgithub.com%2FNadrama-com%2Fgoogle-cloud-setup.git"
            >
              Launch Google Cloud Shell
            </a>
            <p>
              When Cloud Shell opens, run <code>./setup.sh</code> in the
              terminal and follow the prompts.
            </p>
            <p>
              <strong>Alternatively</strong>, run these commands yourself:
            </p>
            <pre class="whitespace-pre-wrap break-words rounded-xl bg-slate-900 p-5 text-sm text-slate-100">{`export PROJECT_ID=your-google-project-id-here\ngcloud config set project $PROJECT_ID\ngcloud services enable compute.googleapis.com cloudresourcemanager.googleapis.com\ngcloud iam service-accounts create nadrama-service-account --display-name="Nadrama"\ngcloud projects add-iam-policy-binding $PROJECT_ID --member=serviceAccount:nadrama-service-account@$PROJECT_ID.iam.gserviceaccount.com --role='roles/compute.admin'\ngcloud iam service-accounts add-iam-policy-binding nadrama-service-account@$PROJECT_ID.iam.gserviceaccount.com --member='serviceAccount:nadrama@nadrama.iam.gserviceaccount.com' --role='roles/iam.serviceAccountTokenCreator'\ngcloud config unset project`}</pre>
          </>
        )}
        <h2 class="mt-4 text-xl font-bold">
          Step 2 of 2: Enter {aws ? "Account" : "Project"} ID &amp; Region
        </h2>
        <Form<ReturnType<typeof providerFormValue>>
          schema={providerFormSchema}
          value={(data) => providerFormValue(data, aws ? "aws" : "google")}
          action={async (d) => {
            await createProvider({
              throwOnError: true,
              responseStyle: "fields",
              path: { account: p.account },
              headers: authSlotHeaders(p.slot),
              body: {
                kind: aws ? "aws" : "google",
                defaultRegion: d.defaultRegion ?? "",
                ...(aws
                  ? { awsAccountId: d.awsAccountId }
                  : { googleProjectId: d.googleProjectId }),
              },
            });
            await queryClient.invalidateQueries({
              queryKey: [p.slot, p.account, "providers"],
            });
            go(`${base}/providers`);
          }}
        >
          <Field
            name={aws ? "awsAccountId" : "googleProjectId"}
            label={aws ? "AWS Account ID" : "Project ID"}
            help={
              aws
                ? "12 digit number. Find it in your AWS account details."
                : "The lowercase alphanumeric and hyphen project ID used above."
            }
            required
            minLength={aws ? 12 : 6}
            maxLength={aws ? 12 : 30}
            pattern={aws ? "[0-9]{12}" : "[a-z][a-z0-9-]{4,28}[a-z0-9]"}
          />
          <div class="field">
            <label for="default-region">Preferred/Default Region</label>
            <select
              class="select select-bordered w-full"
              id="default-region"
              name="defaultRegion"
              value={aws ? "us-east-1" : "us-central1"}
              disabled={providerRegions.isPending || providerRegions.isError}
              required
            >
              <option value="" disabled>
                Select a preferred/default Region...
              </option>
              <For each={providerRegions.data}>
                {(region) => (
                  <option
                    value={region.code}
                    selected={
                      region.code === (aws ? "us-east-1" : "us-central1")
                    }
                  >
                    {region.code} / {region.name}
                  </option>
                )}
              </For>
            </select>
            <p class="help">
              Select your preferred/default region. New resources default to
              this region, but it can be changed.
            </p>
            <Show when={providerRegions.isError}>
              <p class="text-error">Unable to load supported regions.</p>
            </Show>
            <ValidationMessage name="defaultRegion" />
          </div>
        </Form>
      </section>
    </>
  );
}
export function ClusterForm(p: { slot: number; account: string; id?: string }) {
  const go = useGo();
  const queryClient = useQueryClient();
  const base = `/u${p.slot}/${p.account}`;
  const canCreate = useEntitlement("cluster.create");
  if (!p.id && !canCreate())
    return (
      <>
        <Heading title="Create a Podplane Kubernetes PaaS Cluster" />
        <ClusterWaitlistMessage />
      </>
    );
  const [providerID, setProviderID] = createSignal("");
  const [changingProvider, setChangingProvider] = createSignal(false);
  const [region, setRegion] = createSignal("");
  const [changingRegion, setChangingRegion] = createSignal(false);
  const [zone, setZone] = createSignal("");
  const [networkIPv4, setNetworkIPv4] = createSignal(clusterNetworks[0]!);
  const [publicSubnetIPv4, setPublicSubnetIPv4] = createSignal("172.18.0.0/24");
  const [privateSubnetIPv4, setPrivateSubnetIPv4] =
    createSignal("172.18.1.0/24");
  const providers = createQuery(() => ({
    queryKey: [p.slot, p.account, "providers", "cluster-form"],
    queryFn: () =>
      listProviders({
        throwOnError: true,
        responseStyle: "fields",
        path: { account: p.account },
        headers: authSlotHeaders(p.slot),
      }),
  }));
  const projects = createQuery(() => ({
    queryKey: [p.slot, p.account, "projects", "cluster-form"],
    queryFn: async () =>
      (
        await listProjects({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  const provider = () => {
    const items = providers.data?.data?.items ?? [];
    return (
      items.find((item) => item.id === providerID()) ??
      items.find((item) => item.kind === "aws") ??
      items[0]
    );
  };
  const currentRegion = () => region() || provider()?.region || "";
  const providerRegions = createQuery(() => ({
    queryKey: [p.slot, "provider-regions", provider()?.kind],
    enabled: provider()?.kind === "aws" || provider()?.kind === "google",
    queryFn: async () =>
      (
        await listProviderRegions({
          throwOnError: true,
          responseStyle: "fields",
          path: { kind: provider()!.kind as "aws" | "google" },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  const zones = () =>
    providerRegions.data?.find((item) => item.code === currentRegion())
      ?.zones ?? [];
  const currentZone = () => zone() || zones()[0] || `${currentRegion()}a`;
  const noProviders = () =>
    providers.isSuccess &&
    !providers.isFetching &&
    (providers.data.data.items?.length ?? 0) === 0;
  createEffect(() => {
    if (!p.id && noProviders()) void go(`${base}/providers/new`, true);
  });
  return (
    <Show
      when={!providers.isError}
      fallback={
        <div role="alert" class="alert alert-error">
          Cloud providers could not be loaded. Try again shortly.
        </div>
      }
    >
      <Show
        when={
          providers.isSuccess &&
          !providers.isFetching &&
          (p.id || !noProviders())
        }
        fallback={<p>Loading cloud providers…</p>}
      >
        <Heading
          title={
            p.id
              ? "Update Cluster"
              : "Create a Podplane Kubernetes PaaS Cluster"
          }
        />
        <section class="max-w-3xl">
          <Form<ReturnType<typeof clusterFormValue>>
            schema={clusterFormSchema}
            value={(data) => clusterFormValue(data, !p.id)}
            action={async (d) => {
              const body: ClusterCreateInputBody = {
                mode: d.mode as ClusterCreateInputBody["mode"],
                name: d.name ?? "",
                nodes: p.id ? Number(d.nodes) : 1,
                nodesArch: d.nodesArch as ClusterCreateInputBody["nodesArch"],
                projectId: d.projectId ?? "",
                providerId: d.providerId ?? "",
                region: d.region,
                zone: d.zone ?? "",
                networkIpv4: d.networkIpv4 ?? "",
                publicSubnetCidrIpv4: d.publicSubnetCidrIpv4 ?? "",
                privateSubnetCidrIpv4: d.privateSubnetCidrIpv4 ?? "",
              };
              const x = p.id
                ? await updateCluster({
                    throwOnError: true,
                    responseStyle: "fields",
                    path: { account: p.account, id: p.id },
                    headers: authSlotHeaders(p.slot),
                    body,
                  })
                : await createCluster({
                    throwOnError: true,
                    responseStyle: "fields",
                    path: { account: p.account },
                    headers: authSlotHeaders(p.slot),
                    body,
                  });
              await queryClient.invalidateQueries({
                queryKey: [p.slot, p.account, "clusters"],
              });
              go(
                !p.id && d.mode === "plan"
                  ? `${base}/operations/${encodeURIComponent(x.data.operationId)}/status`
                  : `${base}/clusters/${x.data.id ?? p.id}/status?operation=${encodeURIComponent(x.data.operationId)}`,
              );
            }}
            actions={(busy) => (
              <div class="cluster-actions">
                <button
                  class="btn btn-primary"
                  disabled={busy}
                  type="submit"
                  name="mode"
                  value="direct"
                >
                  {busy
                    ? "Working…"
                    : p.id
                      ? "Update Cluster"
                      : "Create Cluster"}
                </button>
                <span>or</span>
                <button
                  class="btn btn-preview"
                  disabled={busy}
                  type="submit"
                  name="mode"
                  value="plan"
                >
                  Preview Infra Changes
                </button>
                <span>or</span>
                <button
                  class="btn btn-outline"
                  disabled={busy}
                  type="button"
                  onClick={() => go(`${base}/clusters`)}
                >
                  Cancel
                </button>
              </div>
            )}
          >
            <div class="cluster-context">
              <div>
                <strong>Cloud Provider/Account</strong>
                <Show
                  when={provider()}
                  fallback={<span>Loading provider…</span>}
                >
                  {(current) => (
                    <Show
                      when={changingProvider()}
                      fallback={
                        <span class="flex items-center gap-2">
                          <input
                            type="hidden"
                            name="providerId"
                            value={current().id}
                          />
                          <Cloud size={18} />
                          {current().kind.toUpperCase()} / {current().awsAccountId ?? current().googleProjectId}{" "}
                          <button
                            class="link"
                            type="button"
                            aria-label="Change cloud provider"
                            onClick={() => setChangingProvider(true)}
                          >
                            (Change)
                          </button>
                        </span>
                      }
                    >
                      <select
                        class="select select-bordered w-full max-w-sm"
                        aria-label="Cloud Provider/Account"
                        name="providerId"
                        value={current().id}
                        onChange={(event) => {
                          setProviderID(event.currentTarget.value);
                          setRegion("");
                          setZone("");
                          setChangingProvider(false);
                        }}
                        required
                      >
                        <For each={providers.data?.data?.items}>
                          {(item) => (
                            <option
                              value={item.id}
                              disabled={item.kind !== "aws"}
                            >
                              {item.kind.toUpperCase()} / {item.awsAccountId ?? item.googleProjectId}
                              {item.kind === "aws"
                                ? ""
                                : " (cluster lifecycle unavailable)"}
                            </option>
                          )}
                        </For>
                      </select>
                    </Show>
                  )}
                </Show>
                <ValidationMessage name="providerId" />
              </div>
              <div>
                <strong>Region</strong>
                <Show
                  when={changingRegion()}
                  fallback={
                    <span>
                      <input
                        type="hidden"
                        name="region"
                        value={currentRegion()}
                      />
                      {currentRegion()}{" "}
                      <button
                        class="link"
                        type="button"
                        aria-label="Change cluster region"
                        onClick={() => setChangingRegion(true)}
                      >
                        (Change)
                      </button>
                    </span>
                  }
                >
                  <select
                    class="select select-bordered w-full max-w-sm"
                    aria-label="Region"
                    name="region"
                    value={currentRegion()}
                    disabled={
                      providerRegions.isPending || providerRegions.isError
                    }
                    onChange={(event) => {
                      setRegion(event.currentTarget.value);
                      setZone("");
                      setChangingRegion(false);
                    }}
                    required
                  >
                    <For each={providerRegions.data}>
                      {(item) => (
                        <option value={item.code}>
                          {item.code} / {item.name}
                        </option>
                      )}
                    </For>
                  </select>
                  <Show when={providerRegions.isError}>
                    <span class="text-error">
                      Unable to load supported regions.
                    </span>
                  </Show>
                </Show>
                <ValidationMessage name="region" />
              </div>
            </div>
            <Show when={p.id}>
              <Field
                name="nodes"
                type="number"
                label="Desired nodes"
                value="1"
                help="The first node in each cluster is free."
                required
                min={1}
                max={100}
              />
            </Show>
            <div class="field">
              <label for="cluster-project">Project</label>
              <select
                class="select select-bordered w-full"
                id="cluster-project"
                name="projectId"
                required
              >
                <For each={projects.data}>
                  {(project) => (
                    <option value={project.id}>{project.name}</option>
                  )}
                </For>
              </select>
              <ValidationMessage name="projectId" />
            </div>
            <Field
              name="name"
              label="Cluster Name"
              placeholder="My Cluster (Production)"
              help="Give your cluster a name"
              required
              minLength={1}
              maxLength={255}
            />
            <details class="cluster-networking">
              <summary>
                <ChevronDown size={18} />
                Show Networking Options (VPC, Zone, &amp; Subnets)
              </summary>
              <div class="mt-4 grid gap-5">
                <p class="cluster-networking-note italic">
                  Cluster networks are dual-stack (IPv4 + IPv6). Specify the
                  IPv4 CIDR blocks below; AWS assigns IPv6 CIDRs automatically.
                </p>
                <div class="field">
                  <label for="network-ipv4">VPC/Network</label>
                  <select
                    class="select select-bordered w-full"
                    id="network-ipv4"
                    name="networkIpv4"
                    value={networkIPv4()}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      const base = value.split(".").slice(0, 2).join(".");
                      setNetworkIPv4(value);
                      setPublicSubnetIPv4(`${base}.0.0/24`);
                      setPrivateSubnetIPv4(`${base}.1.0/24`);
                    }}
                    required
                  >
                    <For each={clusterNetworks}>
                      {(network) => (
                        <option value={network}>{network} (Create)</option>
                      )}
                    </For>
                  </select>
                  <p class="help">
                    Select a recommended /16 IPv4 CIDR for the VPC. AWS may
                    limit how many VPCs you can create.
                  </p>
                  <ValidationMessage name="networkIpv4" />
                </div>
                <div class="field">
                  <label for="cluster-zone">First Zone</label>
                  <select
                    class="select select-bordered w-full"
                    id="cluster-zone"
                    name="zone"
                    value={currentZone()}
                    onChange={(event) => setZone(event.currentTarget.value)}
                    required
                  >
                    <For each={zones()}>
                      {(item) => <option value={item}>{item}</option>}
                    </For>
                  </select>
                  <p class="help">
                    Select the first availability zone for the cluster. More can
                    be added later.
                  </p>
                  <ValidationMessage name="zone" />
                </div>
                <div class="field">
                  <label for="public-subnet-ipv4">
                    First Public Subnet for NAT
                  </label>
                  <input
                    class="input input-bordered w-full"
                    id="public-subnet-ipv4"
                    name="publicSubnetCidrIpv4"
                    value={publicSubnetIPv4()}
                    onInput={(event) =>
                      setPublicSubnetIPv4(event.currentTarget.value)
                    }
                    required
                  />
                  <p class="help">
                    Provide a new /24 IPv4 CIDR within the VPC for outbound
                    internet connectivity using NAT.
                  </p>
                  <ValidationMessage name="publicSubnetCidrIpv4" />
                </div>
                <div class="field">
                  <label for="private-subnet-ipv4">
                    First Private Subnet for Nodes
                  </label>
                  <input
                    class="input input-bordered w-full"
                    id="private-subnet-ipv4"
                    name="privateSubnetCidrIpv4"
                    value={privateSubnetIPv4()}
                    onInput={(event) =>
                      setPrivateSubnetIPv4(event.currentTarget.value)
                    }
                    required
                  />
                  <p class="help">
                    Provide a new /24 IPv4 CIDR within the VPC for cluster
                    nodes.
                  </p>
                  <ValidationMessage name="privateSubnetCidrIpv4" />
                </div>
              </div>
            </details>
            <fieldset class="field">
              <legend class="font-semibold">Default CPU Architecture</legend>
              <div class="cluster-architecture">
                <label>
                  <input
                    class="radio radio-sm"
                    type="radio"
                    name="nodesArch"
                    value="arm64"
                    checked
                    required
                  />{" "}
                  ARM 64-bit (arm64)
                </label>
                <label>
                  <input
                    class="radio radio-sm"
                    type="radio"
                    name="nodesArch"
                    value="amd64"
                    required
                  />{" "}
                  Intel/AMD 64-bit (amd64)
                </label>
              </div>
              <p class="help">
                Specify the CPU architecture to use for your Node
              </p>
              <ValidationMessage name="nodesArch" />
            </fieldset>
          </Form>
        </section>
      </Show>
    </Show>
  );
}
export function Page(p: { slot: number; account: string; rest: string[] }) {
  const go = useGo();
  const routeLocation = useLocation();
  const queryClient = useQueryClient();
  const canCreateCluster = useEntitlement("cluster.create");
  const canManageProviders = useEntitlement("account.settings.manage");
  const section = () => p.rest[0] ?? "";
  const [projectFilter, setProjectFilter] = createSignal(
    new URLSearchParams(location.search).get("project") ?? "",
  );
  const operationId = () =>
    section() === "operations"
      ? p.rest[1]
      : new URLSearchParams(routeLocation().searchStr).get("operation");
  const operation = createQuery(() => ({
    queryKey: [p.slot, p.account, "operation", operationId()],
    queryFn: async () =>
      (
        await getOperation({
          throwOnError: true,
          responseStyle: "fields",
          path: { id: operationId()! },
          headers: authSlotHeaders(p.slot),
        })
      ).data,
    enabled:
      (section() === "clusters" || section() === "operations") &&
      p.rest[2] === "status" &&
      Boolean(operationId()),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state &&
        [
          "succeeded",
          "failed",
          "development_timeout",
          "waiting_approval",
        ].includes(state)
        ? false
        : 2000;
    },
  }));
  const [operationLogs, setOperationLogs] = createSignal<OperationLogBody[]>([]);
  const [logCursor, setLogCursor] = createSignal(0);
  const progress = createMemo(() => operationProgress(operationLogs()));
  let loadedOperation: string | null | undefined;
  createEffect(() => {
    const id = operationId();
    if (id !== loadedOperation) {
      loadedOperation = id;
      setOperationLogs([]);
      setLogCursor(0);
    }
  });
  const logs = createQuery(() => ({
    queryKey: [
      p.slot,
      p.account,
      "operation-logs",
      operationId(),
      logCursor(),
    ],
    queryFn: async () =>
      (
        await getOperationLogs({
          throwOnError: true,
          responseStyle: "fields",
          path: { id: operationId()! },
          query: { after: logCursor() },
          headers: authSlotHeaders(p.slot),
        })
      ).data,
    enabled:
      (section() === "clusters" || section() === "operations") &&
      p.rest[2] === "status" &&
      Boolean(operationId()),
    refetchInterval: () =>
      operation.data?.state &&
      [
        "succeeded",
        "failed",
        "development_timeout",
        "waiting_approval",
      ].includes(operation.data.state)
        ? false
        : 1000,
  }));
  let finalLogRead = "";
  createEffect(() => {
    const page = logs.data;
    if (!page || page.nextCursor <= logCursor()) return;
    setOperationLogs((current) => [...current, ...(page.items ?? [])]);
    setLogCursor(page.nextCursor);
  });
  createEffect(() => {
    const state = operation.data?.state,
      id = operationId(),
      key = `${id}:${state}`;
    if (
      id &&
      state &&
      [
        "succeeded",
        "failed",
        "development_timeout",
        "waiting_approval",
      ].includes(state) &&
      finalLogRead !== key
    ) {
      finalLogRead = key;
      void logs.refetch();
    }
  });
  createEffect(() => {
    if (
      operation.data?.state &&
      ["succeeded", "failed", "development_timeout"].includes(
        operation.data.state,
      )
    )
      void queryClient.invalidateQueries({
        queryKey: [p.slot, p.account, "clusters"],
      });
  });
  const clusterOperations = createQuery(() => ({
    queryKey: [p.slot, p.account, "cluster-operations", p.rest[1]],
    queryFn: async () =>
      (
        await listClusterOperations({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account, id: p.rest[1]! },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
    enabled:
      section() === "clusters" &&
      p.rest[2] === "status" &&
      Boolean(p.rest[1]) &&
      !operationId(),
    refetchInterval: (query) =>
      query.state.data?.some((item) =>
        activeOperationStates.includes(item.state),
      )
        ? 2000
        : false,
  }));
  const clusterResources = createQuery(() => ({
    queryKey: [p.slot, p.account, "cluster-resources", p.rest[1]],
    queryFn: async () =>
      (
        await listClusterResources({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account, id: p.rest[1]! },
          headers: authSlotHeaders(p.slot),
        })
      ).data,
    enabled:
      section() === "clusters" &&
      p.rest[2] === "resources" &&
      Boolean(p.rest[1]),
  }));
  const billing = createQuery(() => ({
    queryKey: [p.slot, p.account, "billing"],
    queryFn: async () =>
      (
        await billingStatus({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data,
    enabled: section() === "billing",
  }));
  const projects = createQuery(() => ({
    queryKey: [p.slot, p.account, "projects", "cluster-filter"],
    queryFn: async () =>
      (
        await listProjects({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
    enabled: section() === "clusters",
  }));
  const clusterProviders = createQuery(() => ({
    queryKey: [p.slot, p.account, "providers", "cluster-list"],
    queryFn: async () =>
      (
        await listProviders({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
    enabled: section() === "clusters" && !p.rest[1],
  }));
  const clusterNeedsProvider = () =>
    clusterProviders.isSuccess && clusterProviders.data.length === 0;
  const lists = [
    "clusters",
    "providers",
    "members",
    "sessions",
    "notifications",
  ];
  const rows = createQuery(() => ({
    queryKey: [
      p.slot,
      p.account,
      section(),
      section() === "clusters" ? projectFilter() : "",
    ],
    queryFn: async () => {
      const options = {
        throwOnError: true as const,
        responseStyle: "fields" as const,
        path: { account: p.account },
        headers: authSlotHeaders(p.slot),
      };
      switch (section()) {
        case "clusters":
          return (
            (
              await listClusters({
                ...options,
                query: projectFilter()
                  ? { project: projectFilter() }
                  : undefined,
              })
            ).data.items ?? []
          );
        case "providers":
          return (await listProviders(options)).data.items ?? [];
        case "members":
          return (await listMembers(options)).data.items ?? [];
        case "sessions":
          return (await listSessions(options)).data.items ?? [];
        default:
          return (await listNotifications(options)).data.items ?? [];
      }
    },
    enabled: lists.includes(section()),
  }));
  const items = () => rows.data ?? [];
  if (section() === "providers" && p.rest[1] === "new")
    return <ProviderNew {...p} kind={p.rest[2]} />;
  if (section() === "clusters" && p.rest[1] === "new")
    return <ClusterForm {...p} />;
  if (section() === "clusters" && p.rest[2] === "update")
    return <ClusterForm {...p} id={p.rest[1]} />;
  if (
    (section() === "clusters" && p.rest[1]) ||
    (section() === "operations" && p.rest[1])
  ) {
    const preview = section() === "operations",
      id = preview ? (operation.data?.clusterId ?? "") : p.rest[1],
      tab = preview ? "status" : (p.rest[2] ?? "status");
    const cluster = () =>
      items().find(
        (item): item is ClusterBody => item.id === id && "nodes" in item,
      );
    return (
      <>
        <Heading
          title={
            preview
              ? "Review your cluster changes"
              : `${cluster()?.name ?? "Cluster"} cluster`
          }
          copy={
            preview
              ? operation.data?.state === "waiting_approval"
                ? "Review the planned changes below, then approve when you’re ready."
                : operation.data?.state === "failed"
                  ? "We couldn’t prepare the plan. No cluster was created."
                  : "We’re preparing a plan so you can review the changes before creating your cluster."
              : undefined
          }
        />
        <Show when={!preview}>
          <div role="tablist" class="cluster-tabs tabs tabs-border mb-6">
            {[
              ["connect", "Connection Info"],
              ["status", "Provisioning"],
              ["resources", "Resources"],
              ["settings", "Settings"],
              ["danger", "Danger Zone"],
            ].map(([key, label]) => (
              <AppLink
                role="tab"
                aria-selected={tab === key}
                class={`tab ${tab === key ? "tab-active" : ""}`}
                to={`/u${p.slot}/${p.account}/clusters/${id}/${key}`}
              >
                {label}
              </AppLink>
            ))}
          </div>
        </Show>
        {tab === "connect" ? (
          <ClusterConnect
            slot={p.slot}
            account={p.account}
            cluster={id}
            name={cluster()?.name ?? id}
            os={p.rest[3]}
          />
        ) : tab === "settings" ? (
          <section class="panel max-w-2xl p-6">
            <h2 class="mb-4 text-xl font-bold">Cluster settings</h2>
            <Show when={cluster()} keyed>
              {(current) => (
                <Form
                  schema={clusterProjectFormSchema}
                  submitLabel="Move cluster"
                  successMessage="Cluster project updated."
                  action={async (data) => {
                    await updateClusterProject({
                      throwOnError: true,
                      responseStyle: "fields",
                      path: { account: p.account, id },
                      headers: authSlotHeaders(p.slot),
                      body: { projectId: data.projectId ?? "" },
                    });
                    await queryClient.invalidateQueries({
                      queryKey: [p.slot, p.account, "clusters"],
                      refetchType: "none",
                    });
                  }}
                >
                  <div class="field">
                    <label for="cluster-settings-project">Project</label>
                    <select
                      class="select select-bordered w-full"
                      id="cluster-settings-project"
                      name="projectId"
                      value={current.projectId}
                      required
                    >
                      <For each={projects.data}>
                        {(project) => (
                          <option value={project.id}>{project.name}</option>
                        )}
                      </For>
                    </select>
                    <ValidationMessage name="projectId" />
                  </div>
                </Form>
              )}
            </Show>
          </section>
        ) : tab === "status" && operationId() ? (
          <section class="panel w-full p-6" aria-live="polite">
            <Show when={!preview}>
              <AppLink
                class="link mb-5 inline-block text-sm"
                to={`/u${p.slot}/${p.account}/clusters/${id}/status`}
              >
                ← All provisioning runs
              </AppLink>
            </Show>
            <Show
              when={!operation.isPending}
              fallback={<p>Loading operation progress…</p>}
            >
              <Show
                when={!operation.isError}
                fallback={
                  <div class="alert alert-error">
                    Operation status could not be loaded. Try again shortly.
                  </div>
                }
              >
                <Show when={operation.data}>
                  {(current) => (
                    <div class="grid gap-4">
                      <div>
                        <h2 class="text-xl font-bold">
                          {preview ? "Cluster plan" : "Latest cluster change"}
                        </h2>
                        <p
                          class={`status mt-2 ${current().state === "failed" ? "text-error" : ""}`}
                        >
                          {current().state === "waiting_approval"
                            ? "Ready to review"
                            : current().state === "running"
                              ? "In progress"
                              : current().state === "enqueued"
                                ? "Queued"
                                : current().state === "failed"
                                  ? "Failed"
                                  : current().state === "development_timeout"
                                    ? "Stopped"
                                    : current().state.replaceAll("_", " ")}
                        </p>
                      </div>
                      <Show
                        when={
                          current().progressMessage || current().progressStage
                        }
                      >
                        <p>
                          {current().state === "failed"
                            ? preview
                              ? "We couldn’t prepare your cluster plan."
                              : "We couldn’t complete your cluster changes."
                            : (current().progressMessage ??
                              current().progressStage)}
                        </p>
                      </Show>
                      <Show when={progress().current}>
                        {(event) => (
                          <div class="operation-progress">
                            <p class="font-medium">
                              {resourceActivity(event())}
                            </p>
                            <Show
                              when={progress().total > 0}
                            >
                              <div class="mt-2">
                                <div class="mb-1 flex justify-between gap-4 text-sm text-slate-600">
                                  <span>
                                    {current().state === "waiting_approval"
                                      ? `${progress().total} resource changes planned`
                                      : `${progress().complete} of ${progress().total} resource changes complete`}
                                  </span>
                                  <Show
                                    when={current().state !== "waiting_approval"}
                                  >
                                    <span>
                                      {Math.round(
                                        (progress().complete /
                                          progress().total) *
                                          100,
                                      )}
                                      %
                                    </span>
                                  </Show>
                                </div>
                                <progress
                                  class="progress progress-primary w-full"
                                  value={
                                    current().state === "waiting_approval"
                                      ? progress().total
                                      : progress().complete
                                  }
                                  max={progress().total}
                                />
                              </div>
                            </Show>
                          </div>
                        )}
                      </Show>
                      <Show when={current().state === "failed"}>
                        <div role="alert" class="alert alert-error">
                          {preview
                            ? "No changes were made. Please try again. If the problem continues, contact support."
                            : "Please try again. If the problem continues, contact support."}
                        </div>
                      </Show>
                      <Show when={current().state === "development_timeout"}>
                        <div class="alert">
                          The local fake-cloud apply reached its safety deadline
                          as expected.
                        </div>
                      </Show>
                      <Show when={current().planCreatedAt}>
                        <dl class="grid gap-2 text-sm md:grid-cols-2">
                          <div>
                            <dt class="font-semibold">Plan created</dt>
                            <dd>
                              {new Date(
                                current().planCreatedAt!,
                              ).toLocaleString()}
                            </dd>
                          </div>
                          <div>
                            <dt class="font-semibold">Desired state</dt>
                            <dd class="break-all">
                              {current().desiredStateDigest}
                            </dd>
                          </div>
                          <Show when={current().openTofuVersion}>
                            <div>
                              <dt class="font-semibold">OpenTofu</dt>
                              <dd>{current().openTofuVersion}</dd>
                            </div>
                          </Show>
                          <Show when={current().podplaneVersion}>
                            <div>
                              <dt class="font-semibold">Podplane</dt>
                              <dd>{current().podplaneVersion}</dd>
                            </div>
                          </Show>
                        </dl>
                      </Show>
                      <Show when={operationLogs().length > 0}>
                        <details class="operation-output">
                          <summary>OpenTofu output</summary>
                          <pre class="operation-terminal" aria-label="OpenTofu output"><For
                              each={operationLogs()}
                            >
                              {(event) => (
                                <span
                                  class={
                                    event.level === "error"
                                      ? "operation-terminal-error"
                                      : ""
                                  }
                                >
                                  {new Date(event.occurredAt).toLocaleTimeString()} {event.message}
                                  {"\n"}
                                </span>
                              )}
                            </For></pre>
                        </details>
                      </Show>
                      <Show when={current().state === "waiting_approval"}>
                        <Button
                          onClick={async () => {
                            const approved = await approveOperation({
                              throwOnError: true,
                              responseStyle: "fields",
                              path: { id: current().id },
                              headers: authSlotHeaders(p.slot),
                              body: {},
                            });
                            await operation.refetch();
                            if (preview) {
                              go(
                                `/u${p.slot}/${p.account}/clusters/${approved.data.clusterId}/status?operation=${encodeURIComponent(current().id)}`,
                              );
                            }
                          }}
                        >
                          Approve plan
                        </Button>
                      </Show>
                    </div>
                  )}
                </Show>
              </Show>
            </Show>
          </section>
        ) : tab === "status" ? (
          <section aria-live="polite">
            <Show
              when={!clusterOperations.isPending}
              fallback={<div class="panel p-6">Loading provisioning runs…</div>}
            >
              <Show
                when={!clusterOperations.isError}
                fallback={
                  <div class="alert alert-error">
                    Provisioning history could not be loaded. Try again shortly.
                  </div>
                }
              >
                <Show
                  when={clusterOperations.data?.length}
                  fallback={
                    <div class="panel p-6">
                      <h2 class="text-lg font-bold">No provisioning runs</h2>
                      <p class="mt-2 text-slate-600">
                        Changes to this cluster will appear here.
                      </p>
                    </div>
                  }
                >
                  <div class="panel overflow-x-auto">
                    <div class="border-b border-base-300 p-5">
                      <h2 class="text-lg font-bold">Provisioning runs</h2>
                      <p class="mt-1 text-sm text-slate-600">
                        Review current progress and previous cluster changes.
                      </p>
                    </div>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Change</th>
                          <th>Started</th>
                          <th>Status</th>
                          <th class="text-right">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={clusterOperations.data}>
                          {(run) => (
                            <tr>
                              <td class="font-medium capitalize">{run.kind}</td>
                              <td>{new Date(run.createdAt).toLocaleString()}</td>
                              <td>
                                <span
                                  class={`status ${run.state === "failed" || run.state === "enqueue_failed" ? "text-error" : run.state === "succeeded" ? "text-success" : ""}`}
                                >
                                  {operationStateLabel(run.state)}
                                </span>
                              </td>
                              <td class="text-right">
                                <AppLink
                                  class="btn btn-ghost btn-sm"
                                  to={`/u${p.slot}/${p.account}/clusters/${id}/status?operation=${encodeURIComponent(run.id)}`}
                                >
                                  View
                                </AppLink>
                              </td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </div>
                </Show>
              </Show>
            </Show>
          </section>
        ) : tab === "resources" ? (
          <section aria-live="polite">
            <Show
              when={!clusterResources.isPending}
              fallback={<div class="panel p-6">Loading deployed resources…</div>}
            >
              <Show
                when={!clusterResources.isError}
                fallback={
                  <div class="alert alert-error">
                    Deployed resources could not be loaded. Try again shortly.
                  </div>
                }
              >
                <Show
                  when={clusterResources.data?.observedAt}
                  fallback={
                    <div class="panel p-6">
                      <h2 class="text-lg font-bold">No resource snapshot yet</h2>
                      <p class="mt-2 text-slate-600">
                        Resources will appear after OpenTofu has written cluster
                        state.
                      </p>
                    </div>
                  }
                >
                  <div class="panel overflow-x-auto">
                    <div class="flex flex-wrap items-start justify-between gap-3 border-b border-base-300 p-5">
                      <div>
                        <h2 class="text-lg font-bold">Deployed resources</h2>
                        <p class="mt-1 text-sm text-slate-600">
                          According to OpenTofu state as of{" "}
                          {new Date(
                            clusterResources.data!.observedAt!,
                          ).toLocaleString()}.
                        </p>
                      </div>
                      <Show when={clusterResources.data?.operationId}>
                        <AppLink
                          class="btn btn-ghost btn-sm"
                          to={`/u${p.slot}/${p.account}/clusters/${id}/status?operation=${encodeURIComponent(clusterResources.data!.operationId!)}`}
                        >
                          View source run
                        </AppLink>
                      </Show>
                    </div>
                    <Show
                      when={
                        clusterResources.data?.operationState &&
                        clusterResources.data.operationState !== "succeeded"
                      }
                    >
                      <div role="alert" class="alert m-5 mb-0">
                        This snapshot was captured after an incomplete
                        provisioning run and may represent partially applied
                        changes.
                      </div>
                    </Show>
                    <Show
                      when={(clusterResources.data?.items?.length ?? 0) > 0}
                      fallback={
                        <p class="p-6 text-slate-600">
                          No managed resources are present in the latest state.
                        </p>
                      }
                    >
                      <table class="table">
                        <thead>
                          <tr>
                            <th>Resource</th>
                            <th>Type</th>
                            <th>Provider</th>
                            <th>Provider ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          <For each={clusterResources.data?.items ?? []}>
                            {(resource) => (
                              <tr>
                                <td class="font-mono text-xs">
                                  {resource.address}
                                </td>
                                <td>{resource.resourceType}</td>
                                <td>{providerLabel(resource.providerName)}</td>
                                <td class="font-mono text-xs break-all">
                                  {resource.providerIdentifier ?? "—"}
                                </td>
                              </tr>
                            )}
                          </For>
                        </tbody>
                      </table>
                    </Show>
                  </div>
                </Show>
              </Show>
            </Show>
          </section>
        ) : tab === "danger" ? (
          <section class="panel border-error p-6">
            <h2 class="text-xl font-bold text-error">Destroy cluster</h2>
            <p class="my-4">
              Compute resources will be destroyed. Your customer-owned state
              bucket and history are retained.
            </p>
            <Button
              kind="btn-error"
              onClick={async () => {
                if (confirm("Destroy this cluster?")) {
                  const result = await destroyCluster({
                    throwOnError: true,
                    responseStyle: "fields",
                    path: { account: p.account, id },
                    headers: authSlotHeaders(p.slot),
                  });
                  go(
                    `/u${p.slot}/${p.account}/clusters/${id}/status?operation=${encodeURIComponent(result.data.operationId)}`,
                  );
                }
              }}
            >
              Destroy cluster
            </Button>
          </section>
        ) : (
          <NotFound />
        )}
      </>
    );
  }
  if (section() === "settings")
    return (
      <>
        <Heading
          title="Account settings"
          copy="Manage your account details and notification recipients."
        />
        <section class="panel p-6">
          <Form<ReturnType<typeof accountSettingsFormValue>>
            schema={accountSettingsFormSchema}
            value={accountSettingsFormValue}
            action={(d) =>
              updateAccount({
                throwOnError: true,
                responseStyle: "fields",
                path: { account: p.account },
                headers: authSlotHeaders(p.slot),
                body: d,
              }).then(() => undefined)
            }
          >
            <Field
              name="name"
              label="Account name"
              required
              minLength={1}
              maxLength={120}
            />
            <Field
              name="organisation"
              label="Organisation/Company Name (optional)"
              maxLength={255}
            />
            <Field
              name="country"
              label="Country"
              required
              minLength={2}
              maxLength={2}
              pattern="[A-Za-z]{2}"
            />
            <Field
              name="notification_emails"
              type="email"
              label="Notification emails"
              help="Add one valid operational recipient per field."
              required
              multiple
            />
          </Form>
        </section>
      </>
    );
  if (section() === "billing")
    return (
      <>
        <Heading
          title="Billing"
          copy="USD $10 per node per month. The first node of each cluster is free."
        />
        <Show when={billing.data} fallback={<p>Loading billing status…</p>}>
          {(status) => (
            <>
              <div class="grid gap-4 md:grid-cols-3">
                <div class="panel p-6">
                  <p class="text-sm text-slate-500">Provider / status</p>
                  <p class="text-lg font-bold">
                    {status().provider} · {status().status}
                  </p>
                </div>
                <div class="panel p-6">
                  <p class="text-sm text-slate-500">Node allowance</p>
                  <p class="text-lg font-bold">
                    {status().billableQuantity} of {status().nodeLimit} billable
                    nodes
                  </p>
                </div>
                <div class="panel p-6">
                  <p class="text-sm text-slate-500">Monthly estimate</p>
                  <p class="text-lg font-bold">
                    ${status().monthlyEstimate.toFixed(2)}{" "}
                    {status().currency.toUpperCase()}
                  </p>
                  <p class="text-sm">
                    ${status().unitPrice.toFixed(2)} per node /{" "}
                    {status().interval}
                  </p>
                </div>
              </div>
              <Show when={status().quantityEffectiveAt}>
                <p class="mt-4">
                  {status().pendingBillableQuantity} billable node
                  {status().pendingBillableQuantity === 1 ? "" : "s"} effective{" "}
                  {new Date(status().quantityEffectiveAt!).toLocaleString()}.
                </p>
              </Show>
              <Button
                disabled={status().provider === "fake"}
                onClick={async () => {
                  const result = await billingPortal({
                    throwOnError: true,
                    responseStyle: "fields",
                    headers: authSlotHeaders(p.slot),
                  });
                  navigateExternal(result.data.provider);
                }}
              >
                Manage payment method
              </Button>
              <Show when={status().provider === "fake"}>
                <p class="help">
                  Payment management is currently unavailable.
                </p>
              </Show>
            </>
          )}
        </Show>
      </>
    );
  if (section() === "members")
    return <MembersPage {...p} members={items() as MemberBody[]} />;
  if (section() === "sessions")
    return <SessionsPage {...p} sessions={items() as SessionBody[]} />;
  if (section() === "clusters" && !canCreateCluster())
    return (
      <>
        <Heading
          title="Clusters"
          copy="Create and manage Podplane clusters across your connected cloud providers."
        />
        <ClusterWaitlistMessage />
      </>
    );
  if (section() === "providers" && !canManageProviders())
    return (
      <>
        <Heading title="Cloud Providers" />
        <AccessNotPermitted>
          You don’t have permission to manage cloud providers. Ask an account
          administrator if you need access.
        </AccessNotPermitted>
      </>
    );
  if (
    section() === "notifications" ||
    ["providers", "clusters"].includes(section())
  )
    return (
      <>
        <Heading
          title={section()[0]!.toUpperCase() + section().slice(1)}
          copy={
            section() === "clusters"
              ? "Create and manage Podplane clusters across your connected cloud providers."
              : section() === "notifications"
                ? "Review account activity and manage unread notifications."
                : undefined
          }
          action={
            <Show
              when={
                section() === "providers" ||
                (section() === "clusters" && clusterProviders.data?.length)
              }
            >
              <Button
                onClick={() =>
                  go(
                    section() === "clusters"
                      ? `/u${p.slot}/${p.account}/clusters/new`
                      : `/u${p.slot}/${p.account}/providers/new`,
                  )
                }
              >
                Add new
              </Button>
            </Show>
          }
        />
        <Show when={section() === "clusters" && !clusterNeedsProvider()}>
          <div class="field mb-5 max-w-sm">
            <label
              class="text-xs uppercase tracking-wider text-slate-500"
              for="project-filter"
            >
              Project
            </label>
            <select
              class="select select-bordered w-full"
              id="project-filter"
              value={projectFilter()}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setProjectFilter(value);
                const url = new URL(location.href);
                if (value) url.searchParams.set("project", value);
                else url.searchParams.delete("project");
                go(`${url.pathname}${url.search}`, true);
              }}
            >
              <option value="">All projects</option>
              <For each={projects.data}>
                {(project) => (
                  <option value={project.id}>{project.name}</option>
                )}
              </For>
            </select>
          </div>
        </Show>
        <Show
          when={section() !== "clusters" || !clusterNeedsProvider()}
          fallback={
            <>
              <hr class="border-slate-200" />
              <div class="grid justify-items-center gap-5 py-10 text-center">
                <p class="text-slate-500">
                  You cannot create a cluster until you have connected a cloud
                  provider.
                </p>
                <AppLink
                  class="btn btn-primary"
                  to={`/u${p.slot}/${p.account}/providers/new`}
                >
                  Connect a Cloud Provider
                </AppLink>
              </div>
            </>
          }
        >
          <Rows
            rows={items()}
            onDisconnect={
              section() === "providers"
                ? async (id) => {
                    if (
                      !confirm(
                        "Disconnect this cloud provider? Existing clusters may stop being manageable.",
                      )
                    )
                      return;
                    await deleteProvider({
                      throwOnError: true,
                      responseStyle: "fields",
                      path: { account: p.account, id },
                      headers: authSlotHeaders(p.slot),
                    });
                    await queryClient.invalidateQueries({
                      queryKey: [p.slot, p.account, "providers"],
                    });
                  }
                : undefined
            }
          />
        </Show>
      </>
    );
  return (
    <>
      <Heading
        title="Welcome to Nadrama"
        copy="The fastest and easiest way to deploy a Kubernetes PaaS in your AWS or Google Cloud account."
      />
      <Button onClick={() => go(`/u${p.slot}/${p.account}/clusters/new`)}>
        Create your first cluster
      </Button>
    </>
  );
}
